/**
 * The game controller: turns the pure simulation into something a screen can show and act on. It owns the save, the game
 * clock (real time plus an optional debug offset) and the short-lived UI state (toasts, the mood after a care action).
 * It never touches the DOM. See docs/handoff/004-game-view.md for the contract with the screen.
 */
import {
  ACHIEVEMENTS,
  DRIP_WEEKS,
  checkAchievements,
  dripActive,
  isItemGranted,
  weeksBetween,
  type RewardLook,
  MAX_SCORE,
  METER_IDS,
  animalCurves,
  animalRefillsMs,
  canHatch,
  emptySave,
  highWaterMark,
  petStateAt,
  recordCare,
  recordHeartbeat,
  recordOpen,
  recordWake,
  scoreAt,
  weekBounds,
  weekIdFor,
  type CurveProfile,
  type MeterId,
  type PetLog,
  type SaveGame,
} from "@zookeepbit/sim";
import type {
  BookEntryView,
  GameViewProps,
  LookKind,
  LookOptionView,
  LooksView,
  MenuId,
  MeterView,
  Mood,
  PreviousPetView,
  Screen,
  SettingKey,
  SettingsView,
  ToastView,
} from "../ui/view-model";
import { CATALOGUE, canBeNone, effectiveLock, lockMessage, type Lock } from "./catalogue";

export interface Clock {
  now(): number;
}

export interface SaveStore {
  load(): SaveGame;
  save(game: SaveGame): void;
}

/** `kind:index` of a catalogue entry, or null. */
function shopItem(id: string): { kind: LookKind; index: number } | null {
  const [kind, raw] = id.split(":");
  const index = Number(raw);
  if (!kind || !Object.prototype.hasOwnProperty.call(CATALOGUE, kind) || !Number.isInteger(index)) return null;
  return CATALOGUE[kind as LookKind][index] ? { kind: kind as LookKind, index } : null;
}

export interface GameOptions {
  clock: Clock;
  store: SaveStore;
  /** IANA time zone of the player, for example from Intl. */
  timeZone: string;
  /** Include the debug controls in the view. */
  debug?: boolean;
  /** How many weeks from the first hatch badges also give items (default 12; shorter for testing). */
  dripWeeks?: number;
  /**
   * The owner's tuned care numbers for the shark (docs/plans/016-care-curves-admin.md), read when a pet hatches and frozen into its log.
   * Null or missing: the built-in numbers.
   */
  careConfig?: () => { curves: Record<MeterId, Partial<CurveProfile>>; refillMs: Record<MeterId, number> } | null;
}

/** What the screen needs, without the callbacks (the hook adds those). */
export type GameView = Omit<
  GameViewProps,
  "onMenu" | "onCare" | "onHatch" | "onDismissToast" | "onCloseBook" | "onVisit" | "onLeaveVisit" | "onPickLook" | "onSetting" | "onRename" | "debug"
>;

const SPECIES = "shark";
const REACTION_MS = 2_500;
const TOAST_MS = 4_000;
/** docs/SPEC.md section 3: 1 to 12 letters or numbers. */
const NAME_PATTERN = /^[\p{L}\p{N}]{1,12}$/u;
/** A heartbeat later than this after the last one means the app was closed in between. */
const SESSION_GAP_MS = 3 * 60_000;

const CARE_TOAST: Record<MeterId, string> = {
  food: "Yum! Fed.",
  rest: "Resting nicely.",
  fun: "That was fun!",
  clean: "Sparkling clean.",
  love: "Feeling loved.",
};

/**
 * How long each care action takes to refill its meter comes from the pet's own log (frozen at hatch, docs/SPEC.md section 3);
 * a pet saved before that was stored uses the built-in times (Rest is a sleep of five minutes).
 */
const BUILT_IN_FILL_MS = animalRefillsMs(SPECIES);

const CARE_MOOD: Record<MeterId, Mood> = {
  food: "eating",
  rest: "resting",
  fun: "happy",
  clean: "happy",
  love: "happy",
};

/** What a visited pet says when the player cares for it: no meters, nothing is saved (docs/SPEC.md section 6). */
const VISIT_TOAST = (name: string): Record<MeterId, string> => ({
  food: `${name} enjoyed the snack.`,
  rest: `${name} is having a nap.`,
  fun: `${name} loves to play!`,
  clean: `${name} is sparkling.`,
  love: `${name} feels loved.`,
});

const isBool = (v: unknown) => typeof v === "boolean";
/** What each setting accepts. Anything else is ignored. */
const SETTING_VALID: Record<SettingKey, (v: unknown) => boolean> = {
  controls: (v) => v === "touch" || v === "retro",
  meterStyle: (v) => v === "bars" || v === "numbers",
  meterLabels: (v) => v === "icons" || v === "names",
  largeText: isBool,
  sound: isBool,
  reduceMotion: (v) => v === null || isBool(v),
  notifications: isBool,
};

function looksView(l: SaveGame["looks"]): LooksView {
  return {
    shellColour: l.shellColour,
    shellPattern: l.shellPattern,
    background: l.background,
    toy: l.toy,
    companion: l.companion,
    decoration: l.decoration,
  };
}

const LOOK_LABEL: Record<RewardLook, string> = { companion: "companion", decoration: "decoration", shellColour: "shell colour" };

function optionsView(looks: LooksView, lockOf: (kind: LookKind, index: number) => Lock): Record<LookKind, LookOptionView[]> {
  const out = {} as Record<LookKind, LookOptionView[]>;
  for (const kind of Object.keys(CATALOGUE) as LookKind[]) {
    const list: LookOptionView[] = CATALOGUE[kind].map((e, index) => ({ index, name: e.name, lock: lockOf(kind, index), selected: looks[kind] === index }));
    out[kind] = canBeNone(kind) ? [{ index: -1, name: "None", lock: null, selected: looks[kind] === -1 }, ...list] : list;
  }
  return out;
}

interface Toast {
  id: number;
  text: string;
  until: number;
}

export function createGame(options: GameOptions) {
  const { clock, store, timeZone } = options;
  let save: SaveGame = store.load();
  let offset = 0;
  let reaction: { mood: Mood; until: number } | null = null;
  let toasts: Toast[] = [];
  let nextToastId = 1;
  let seen = { lastChance: false, alive: true };
  /** Which view is open. Not saved: the app always starts on the shell. */
  let screen: Screen = "game";
  let visitId: string | null = null;
  /** Items edit mode (the three spots are highlighted). Not saved. */
  let editing = false;

  const toEntryView = (e: SaveGame["book"][number]): BookEntryView => ({
    id: e.weekId,
    name: e.name,
    species: e.species,
    points: Math.floor(e.points),
    weekLabel: `Week ${Number(e.weekId.split("-W")[1])}`,
    looks: looksView(e.looks),
  });

  /** The Monday sequence data: last week's pet, while the new egg has not hatched. */
  function previousPet(at: number): PreviousPetView | null {
    const old = save.pet;
    if (!old || currentPet(at)) return null;
    const result = scoreAt(old, weekBounds(old.weekId, old.timeZone).end);
    return {
      name: save.petName ?? "",
      species: old.species,
      points: Math.floor(result.points),
      outcome: result.entry && !result.lost ? "book" : "lost",
    };
  }

  /** The game time never goes backwards: a rewound device clock cannot lower the score or undo care. */
  let floor = 0;
  const now = () => {
    floor = Math.max(floor, clock.now() + offset, save.pet ? highWaterMark(save.pet) : 0);
    return floor;
  };
  const dripWeeks = options.dripWeeks ?? DRIP_WEEKS;
  const dripOpen = (at: number) => dripActive(save.startedWeekId, weekIdFor(at, timeZone), dripWeeks);
  /** What the signed-in account has bought (`kind:index`); never saved on the device, so signing out locks them again. */
  let owned = new Set<string>();
  const lockOf = (at: number) => (kind: LookKind, index: number): Lock => {
    const entry = CATALOGUE[kind][index];
    return entry ? effectiveLock(entry, isItemGranted(save, kind, index), dripOpen(at), owned.has(`${kind}:${index}`)) : null;
  };

  /** Records badges that were just earned and tells the player once. Returns true when the save changed. */
  function earnBadges(at: number): boolean {
    const result = checkAchievements(save, weekIdFor(at, timeZone), dripWeeks);
    if (result.save === save) return false;
    save = result.save;
    for (const a of result.added) {
      const gift = a.reward.kind === "item" && a.granted ? ` You got the ${CATALOGUE[a.reward.look][a.reward.index]?.name ?? "item"}!` : "";
      toast(`New badge: ${a.name}.${gift}`.replace(/\.$/, ""), at);
    }
    return true;
  }

  const persist = () => {
    earnBadges(now());
    store.save(save);
  };

  /** Marks the app as open at `at`: extends the running session if it was seen a moment ago, else starts a new one. */
  function touch(pet: PetLog, at: number): PetLog {
    const lastSeen = pet.sessions.reduce((m, s) => Math.max(m, s.lastSeenAt), -Infinity);
    return at - lastSeen <= SESSION_GAP_MS ? recordHeartbeat(pet, at) : recordOpen(pet, at);
  }

  /** The pet of the current week, or null (never hatched, or last week's pet). */
  function currentPet(at: number): PetLog | null {
    const pet = save.pet;
    return pet && pet.weekId === weekIdFor(at, timeZone) ? pet : null;
  }

  function toast(text: string, at: number) {
    toasts = [...toasts.filter((t) => t.until > at), { id: nextToastId++, text, until: at + TOAST_MS }];
  }

  /** Notice changes since the last look and tell the player once. */
  function observe(at: number, announce: boolean) {
    const pet = currentPet(at);
    if (!pet) {
      seen = { lastChance: false, alive: true };
      return;
    }
    const state = petStateAt(pet, at);
    if (announce) {
      if (state.lastChance && !seen.lastChance) toast(`${save.petName ?? "Your pet"} needs you now!`, at);
      if (!state.alive && seen.alive) toast(`${save.petName ?? "Your pet"} didn't make it. The points stay.`, at);
    }
    seen = { lastChance: state.lastChance, alive: state.alive };
  }

  function update(pet: PetLog, at: number) {
    save = { ...save, pet };
    persist();
    observe(at, true);
  }

  return {
    now,

    /** Adopts another save (the cloud's) while the game is running: stored, shown, and every short-lived screen state cleared. */
    loadSave(next: SaveGame) {
      save = next;
      persist();
      reaction = null;
      screen = "game";
      visitId = null;
      editing = false;
      toasts = [];
      observe(now(), false);
    },

    /** Call when the app opens or comes back to the front. */
    open() {
      save = store.load();
      const at = now();
      const pet = currentPet(at);
      if (pet) {
        save = { ...save, pet: recordOpen(pet, at) };
        persist();
      }
      if (earnBadges(at)) store.save(save);
      observe(at, false);
    },

    /** Call about once a minute while the app is open. */
    tick() {
      const at = now();
      const pet = currentPet(at);
      if (pet) update(touch(pet, at), at);
    },

    /** What a tap did: "cared" (the action went ahead), "woke" (Rest on a sleeping shark only wakes it) or "ignored" (nothing happened, so no sound either). */
    care(meter: MeterId): "cared" | "woke" | "ignored" {
      const at = now();
      if (screen === "visit") {
        const visited = save.book.find((e) => e.weekId === visitId);
        if (!visited) return "ignored";
        reaction = { mood: CARE_MOOD[meter], until: at + REACTION_MS };
        toast(VISIT_TOAST(visited.name)[meter], at);
        return "cared";
      }
      const pet = currentPet(at);
      if (editing || !pet) return "ignored";
      const current = petStateAt(pet, at);
      if (!current.alive) return "ignored";
      // Any action wakes a sleeping shark: Rest stops climbing where it is (the owner's call, 2026-10-20). Pressing Rest itself
      // while asleep is the way to wake up, and does not start a new sleep.
      const sleeping = current.meters.rest.refilling;
      const awake = sleeping ? recordWake(touch(pet, at), "rest", at) : touch(pet, at);
      if (meter === "rest" && sleeping) {
        update(awake, at);
        reaction = null;
        toast("Awake.", at); // replaces "Resting nicely." instead of leaving it up
        return "woke";
      }
      if (current.meters[meter].refilling && meter !== "rest") return "ignored"; // a meter that is still climbing ignores a second tap
      update(recordCare(awake, meter, at, pet.refills?.[meter] ?? BUILT_IN_FILL_MS[meter]), at);
      reaction = { mood: CARE_MOOD[meter], until: at + REACTION_MS };
      toast(CARE_TOAST[meter], at);
      return "cared";
    },

    hatch(rawName: string) {
      const name = rawName.trim();
      const at = now();
      if (!NAME_PATTERN.test(name)) return;
      if (currentPet(at) || !canHatch(save.lastHatchedWeekId, at, timeZone)) return;

      let book = save.book;
      const old = save.pet;
      if (old) {
        const result = scoreAt(old, weekBounds(old.weekId, old.timeZone).end);
        if (result.entry && !result.lost) {
          book = [
            ...book,
            { weekId: old.weekId, species: old.species, name: save.petName ?? "", points: Math.floor(result.points), looks: { ...save.looks } },
          ];
        }
      }

      const weekId = weekIdFor(at, timeZone);
      const tuned = options.careConfig?.() ?? null;
      const pet: PetLog = {
        species: SPECIES,
        hatchedAt: at,
        weekId,
        timeZone,
        curves: tuned ? tuned.curves : animalCurves(SPECIES),
        refills: tuned ? { ...tuned.refillMs } : animalRefillsMs(SPECIES),
        care: [],
        sessions: [{ openAt: at, lastSeenAt: at }],
      };
      save = { ...save, pet, petName: name, lastHatchedWeekId: weekId, book };
      persist();
      reaction = null;
      screen = "game";
      visitId = null;
      seen = { lastChance: false, alive: true };
    },

    /** The Items and Book buttons change the game screen; the Settings, Friends, Badges and Shop sheets are drawn by the screen itself. */
    menu(id: MenuId) {
      if (id === "settings" || id === "friends" || id === "shop" || id === "badges" || id === "trophies" || id === "more" || id === "save") return;
      if (id === "items") {
        // The Items button toggles edit mode on the game screen only (not in the book or a visit).
        if (screen === "game") editing = !editing;
        return;
      }
      if (id === "book") {
        // The Book button toggles: pressing it with the book (or a visit) open puts the shell back.
        editing = false;
        screen = screen === "game" ? "book" : "game";
        visitId = null;
        reaction = null;
        return;
      }
    },

    /** The shop items the account owns (ids like "toy:2"); anything that is not an item is ignored. */
    setOwned(ids: readonly string[]) {
      owned = new Set(ids.filter((id) => shopItem(id) !== null));
    },

    /** Whether the shop should offer this item now: it exists, is locked for the shop and is not yet owned. */
    shopOffered(id: string): boolean {
      const item = shopItem(id);
      return item !== null && lockOf(now())(item.kind, item.index) === "shop";
    },

    /** Change one setting. A value that is not allowed, or a key that does not exist, is ignored. */
    setSetting<K extends SettingKey>(key: K, value: SettingsView[K]) {
      if (!Object.prototype.hasOwnProperty.call(SETTING_VALID, key) || !SETTING_VALID[key](value)) return;
      save = { ...save, settings: { ...save.settings, [key]: value } };
      persist();
    },

    /** Rename this week's pet: the same rule as at hatching (1 to 12 letters or numbers). */
    rename(rawName: string) {
      const name = rawName.trim();
      if (!NAME_PATTERN.test(name) || !currentPet(now())) return;
      save = { ...save, petName: name };
      persist();
    },

    /** Pick a look. A locked option only says how to get it; an index that does not exist is ignored. */
    pickLook(kind: LookKind, index: number) {
      const entry = CATALOGUE[kind][index];
      const empty = index === -1 && canBeNone(kind);
      if (!Number.isInteger(index) || (!entry && !empty)) return;
      const at = now();
      const lock = entry ? lockOf(at)(kind, index) : null;
      if (lock) {
        toast(lockMessage(lock), at);
        return;
      }
      save = { ...save, looks: { ...save.looks, [kind]: index } };
      persist();
      if (canBeNone(kind)) toast(`${entry?.name ?? "None"} placed`, at);
    },

    /** Leave the book (or a visit) and bring the shell back. */
    closeBook() {
      screen = "game";
      visitId = null;
      reaction = null;
    },

    /** Open a book pet's habitat. Only from the book; an unknown id is ignored. */
    visit(id: string) {
      if (screen !== "book" || !save.book.some((e) => e.weekId === id)) return;
      screen = "visit";
      visitId = id;
      reaction = null;
    },

    /** Leave a visit: the book opens again. */
    leaveVisit() {
      if (screen !== "visit") return;
      screen = "book";
      visitId = null;
      reaction = null;
    },

    dismissToast(id: number) {
      toasts = toasts.filter((t) => t.id !== id);
    },

    save: () => save,

    view(): GameView {
      const at = now();
      const pet = currentPet(at);
      const state = pet ? petStateAt(pet, at) : null;
      const meters: MeterView[] = METER_IDS.map((id) => {
        const value = state ? Math.round(state.meters[id].value) : 100;
        return { id, value, low: value <= 40, critical: value <= 20, refilling: state?.meters[id].refilling ?? false };
      });
      const sleeping = state?.alive === true && state.meters.rest.refilling;
      const refillingMeter = METER_IDS.find((id) => state?.meters[id].refilling);
      const status = !pet ? "no-pet" : state?.alive ? "alive" : "dead";

      let mood: Mood = "idle";
      if (status === "dead") mood = "dead";
      else if (sleeping) mood = "resting"; // the whole refill, also after a reload
      else if (reaction && at < reaction.until) mood = reaction.mood;
      else if (refillingMeter) mood = CARE_MOOD[refillingMeter];
      else if (state?.lastChance) mood = "lastChance";
      else if (meters.every((m) => m.value >= 70)) mood = "happy";
      // A visited pet is always in a good mood: it has no meters.
      if (screen === "visit") mood = reaction && at < reaction.until ? reaction.mood : "happy";

      const visible: ToastView[] = toasts.filter((t) => t.until > at).map(({ id, text }) => ({ id, text }));
      const { controls, meterStyle, meterLabels, largeText, sound, reduceMotion, notifications } = save.settings;
      return {
        status,
        species: pet?.species ?? SPECIES,
        petName: pet ? (save.petName ?? "") : "",
        meters,
        // Whole points only, rounded down so the number never jumps back up and down.
        score: pet ? Math.floor(scoreAt(pet, at).points) : 0,
        maxScore: MAX_SCORE,
        mood,
        sleeping,
        settings: { controls, meterStyle, meterLabels, largeText, sound, reduceMotion, notifications },
        reducedMotion: reduceMotion ?? false,
        looks: looksView(save.looks),
        options: optionsView(looksView(save.looks), lockOf(at)),
        badges: ACHIEVEMENTS.map((a) => {
          const earned = save.achievements.find((e) => e.id === a.id);
          const item = a.reward.kind === "item" ? `${CATALOGUE[a.reward.look][a.reward.index]?.name ?? "Item"} ${LOOK_LABEL[a.reward.look]}` : "Badge";
          return { id: a.id, name: a.name, text: a.text, earned: earned !== undefined, granted: earned?.granted ?? false, rewardText: item };
        }),
        dripWeeksLeft: save.startedWeekId === null ? null : Math.max(0, dripWeeks - weeksBetween(save.startedWeekId, weekIdFor(at, timeZone))),
        editing,
        friendBadge: 0,
        toasts: visible,
        screen,
        book: save.book.map(toEntryView),
        visiting: screen === "visit" ? (save.book.filter((e) => e.weekId === visitId).map(toEntryView)[0] ?? null) : null,
        previous: pet ? null : previousPet(at),
      };
    },

    debug: {
      enabled: options.debug === true,
      advance(ms: number) {
        if (ms > 0) offset += ms;
        observe(now(), true);
      },
      /** Start over: real time again and a fresh save (a log with future events could not be replayed in real time). */
      reset() {
        offset = 0;
        floor = 0;
        save = emptySave();
        persist();
        reaction = null;
        screen = "game";
        visitId = null;
        editing = false;
        toasts = [];
        seen = { lastChance: false, alive: true };
      },
      nowLabel(): string {
        return new Intl.DateTimeFormat("en-GB", {
          timeZone,
          weekday: "short",
          day: "numeric",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
          hourCycle: "h23",
        })
          .format(now())
          .replace(",", "");
      },
    },
  };
}

export type Game = ReturnType<typeof createGame>;
